/**
 * @lines98/client/components/setting-row — reusable «строка настройки» (label +
 * optional description + control).
 *
 * ОБЩИЙ модуль (owner-directive «код через модули» + brief-39 §C «reusable-компонент
 * строки настройки»). Это «задел»: добавить новую настройку (дальтоники / темы /
 * звук — БУДУЩЕЕ, НЕ сейчас) = создать ещё одну строку этим же компонентом и вставить
 * её в `settings-screen`, БЕЗ переделки раскладки экрана.
 *
 * Структура строки:
 *   [ label (+ «?»-popover для описания на touch) ............ control ]
 *
 * Label — **must-visible на всех viewport** (skill §0.B: labels / названия —
 * никогда не прячем). Описание (descriptive copy) — опционально; по §0.B + §0.E:
 *   - touch: за «?»-иконкой (InfoPopover, появляется рядом с label);
 *   - ПК: inline под label (CSS `.setting-row-desc` показывается на
 *     `(hover:hover) and (pointer:fine)`, «?» скрыт).
 * Так обеспечивается visibility-first без обрезки и без дубля-логики по экранам.
 *
 * Control — любой интерактивный элемент (сейчас toggle-switch; в будущем select /
 * slider — тот же слот). Передаётся как готовый HTMLElement.
 *
 * CSS — co-located `setting-row.css`.
 */

import './setting-row.css';

import { mountInfoPopover, type InfoPopoverHandle } from './info-popover.js';
import { createPanelRow } from './panel-row.js';

export interface SettingRowOptions {
  /** Подпись настройки — must-visible на всех viewport (skill §0.B). */
  readonly label: string;
  /**
   * Описание-пояснение (опционально). На touch уходит за «?»-popover, на ПК
   * показывается inline под label. Если не задано — ни «?», ни inline-копии нет.
   */
  readonly description?: string;
  /** Заголовок «?»-popover'а на touch (по умолчанию = label). */
  readonly popoverTitle?: string;
  /** Готовый интерактивный контрол (toggle-switch.element / select / …). */
  readonly control: HTMLElement;
}

export interface SettingRowHandle {
  /** Корневой элемент строки — потребитель вставляет в список настроек. */
  readonly element: HTMLElement;
  /** Освободить ресурсы (popover). Зовётся при unmount экрана. */
  destroy(): void;
}

/**
 * Создать строку настройки. Возвращает handle с `.element` для монтирования в
 * список настроек экрана.
 */
export function createSettingRow(opts: SettingRowOptions): SettingRowHandle {
  // --- Текстовая часть (label + опц. описание/«?») — главный слот panel-row ---
  const text = document.createElement('div');
  text.className = 'setting-row-text';

  const labelLine = document.createElement('div');
  labelLine.className = 'setting-row-label-line';

  const label = document.createElement('span');
  label.className = 'setting-row-label';
  label.textContent = opts.label;
  labelLine.appendChild(label);

  text.appendChild(labelLine);

  let popover: InfoPopoverHandle | null = null;
  if (opts.description) {
    // ПК: inline-копия под label (показывается CSS-медиа на mouse-устройствах).
    const desc = document.createElement('p');
    desc.className = 'setting-row-desc';
    desc.textContent = opts.description;
    text.appendChild(desc);

    // touch: «?»-popover рядом с label (скрыт на ПК через CSS-медиа компонента).
    popover = mountInfoPopover({
      title: opts.popoverTitle ?? opts.label,
      bodyText: opts.description,
      triggerHost: labelLine,
      variant: '?',
    });
  }

  // Плашка-строка — примитив panel-row (геометрия + «толщина от контрола»).
  // setting-row = тонкая ветка: наполняет слоты, добавляет только текст-вид.
  const row = createPanelRow({
    main: text,
    control: opts.control,
    className: 'setting-row',
  }).element;

  return {
    element: row,
    destroy(): void {
      popover?.unmount();
    },
  };
}
